/**
 * Pure colour resolution for ActionCard.
 *
 * Deliberately dependency-free: ActionCards.jsx imports the components barrel
 * (`../index`), which drags in most of the app, so anything that needs a fast unit
 * test has to live outside it.
 */

// Urgency state constants.
export const URGENCY_STATES = {
    URGENT: 'urgent',
    WARNING: 'warning',
    GOOD: 'good',
    NEUTRAL: 'neutral',
};

// The unselected card's border, from getCardStyle's baseStyle.
export const UNSELECTED_BORDER_COLOR = '#e2e8f0';

// Chip/dot tones shared with the Launchpad "Your pipeline" trend chip
// (LaunchpadPipeline.jsx TrendChip) so every Overdue / on-track chip and dot across the
// Launchpad cards, the four table strips and the pipeline row is the SAME red / green.
// Amber has no pipeline equivalent, so the existing due-today tokens stay.
export const CHIP_RED = { text: '#d23b50', bg: '#fdeef0' };
export const CHIP_GREEN = { text: '#0b8a5f', bg: '#e7f8f1' };

export const URGENCY_COLORS = {
    [URGENCY_STATES.URGENT]: CHIP_RED.text,
    [URGENCY_STATES.WARNING]: '#f59e0b',
    [URGENCY_STATES.GOOD]: CHIP_GREEN.text,
    [URGENCY_STATES.NEUTRAL]: UNSELECTED_BORDER_COLOR,
};

// Focus ring colour used by getCardStyle's '&:focus-visible' outline. Declared here
// only so SELECTED_NEUTRAL_COLOR can be asserted different from it: the focus outline
// sits 2px OUTSIDE the border, so if the two share a colour a focused+selected card
// renders as two concentric rings of the same blue and reads as a doubled border.
export const FOCUS_RING_COLOR = '#2563eb';

// Selection ring for cards whose urgency state is NEUTRAL. Slate ink rather than a hue,
// so it can't be mistaken for an urgency signal (red/amber/green) or for the blue focus
// ring, while still being obviously darker than UNSELECTED_BORDER_COLOR.
export const SELECTED_NEUTRAL_COLOR = '#475569';

/**
 * Colour of the ring/tint that marks a card as SELECTED.
 *
 * Selection is a different state from urgency and must not be derived from it.
 * URGENCY_STATES.NEUTRAL's colour is UNSELECTED_BORDER_COLOR, so a selected NEUTRAL
 * card used to differ from an unselected one only by a 2px lift and a ~12%-alpha
 * shadow — invisible in practice. getUrgencyState() returns NEUTRAL for ANY card whose
 * count is 0, so this hit every board that pre-selects its first card on landing: on
 * Booked, "Upcoming this week" reads 0 in a week with no consultations, so the
 * pre-selected card looked unselected, the table looked unscoped, and the first click —
 * which actually CLEARS the scope — read as "selecting it", revealing every booked lead.
 *
 * Non-NEUTRAL cards keep their urgency-coloured selection unchanged.
 */
export const resolveSelectionColor = (urgencyState) => {
    // NEUTRAL, and any state we don't recognise, get the dedicated selection colour:
    // an unknown urgency must never be able to make the selection invisible again.
    if (urgencyState === URGENCY_STATES.NEUTRAL) return SELECTED_NEUTRAL_COLOR;
    return URGENCY_COLORS[urgencyState] || SELECTED_NEUTRAL_COLOR;
};

/**
 * Task O — stage accent colours for the "due" cards (New-lead "Tasks due", Booked
 * "Awaiting outcome"/"Upcoming", Attended "Due now", Started "Due now", and the
 * Launchpad "Needs you today" row's 4 mirrors of those same cards).
 *
 * The user's feedback was "far too much red" on these rows: each card's headline
 * number and left accent bar were driven by `urgencyState` (URGENT/WARNING →
 * red/amber), so a backlog of overdue tasks painted the whole row red. These
 * cards now take a fixed per-STAGE accent (icon background + a thin top border —
 * see ActionCard's `card.accentColor`) instead, and their headline number reverts
 * to plain dark text regardless of count. RED stays reserved for "Overdue" chips
 * only.
 *
 * Copied verbatim from the pipeline row's own stage colours
 * (src/constants/reportStatusPalette.js STATUS_CONFIG.{NEW,CONSULTATION_BOOKED,
 * CONSULTATION_ATTENDED,TREATMENT_STARTED}.color) — not invented here, so the
 * "due" cards visually match "Your pipeline" above them.
 */
export const STAGE_ACCENT_COLORS = {
    NEW: '#3b82f6',
    BOOKED: '#8b5cf6',
    ATTENDED: '#f59e0b',
    STARTED: '#10b981',
};

// Amber / grey chip tones. Amber matches the 'due-today' badge type in ActionCards'
// getTimeBadgeStyle; grey matches 'info'. TrendPill uses them so a NEGATIVE trend is
// amber, not red — red is reserved for overdue / late.
export const CHIP_AMBER = { text: '#d97706', bg: '#fef3c7' };
export const CHIP_GREY = { text: '#64748b', bg: '#f1f5f9' };

/**
 * Reporting-page accents by metric MEANING (not by position in a grid), so "Leads" is
 * the same blue on every report. The first four are the pipeline stage colours
 * (STAGE_ACCENT_COLORS); spend/lost are neutral slates. Flat colours — no gradients.
 */
export const REPORT_METRIC_ACCENTS = {
    leads: STAGE_ACCENT_COLORS.NEW,
    booked: STAGE_ACCENT_COLORS.BOOKED,
    attended: STAGE_ACCENT_COLORS.ATTENDED,
    started: STAGE_ACCENT_COLORS.STARTED,
    spend: '#64748b',
    lost: '#94a3b8',
};
